import { Checkbox, Menu, MenuItem, Typography } from "@mui/material";
import { type ReactNode, useCallback, useMemo } from "react";

import { FilterChip } from "~/components/UI/FilterChip";
import { useFilterDropdownMultiple } from "~/components/UI/FilterDropdown/useFilterDropdownMultiple";
import { FilterMenuTitle } from "~/components/UI/FilterMenuTitle";
import type { IOption } from "~/types/option";

import * as Styled from "./FilterDropdown.styles";

export interface FilterMultiSelectDropdownProps<TValue = string> {
  label: string;
  icon?: ReactNode;
  options: IOption<TValue>[];
  value: TValue[];
  onChange: (value?: TValue[]) => void;
  minOptionCount?: number;
  exclude?: boolean;
  getChipText?: (values: TValue[]) => string;
}

export const FilterDropdownMultiple = <TValue,>({
  label,
  icon,
  options,
  value,
  onChange,
  minOptionCount = 1,
  exclude = false,
  getChipText,
}: FilterMultiSelectDropdownProps<TValue>) => {
  const showAllOption = minOptionCount > 0;
  const {
    anchorEl,
    open,
    isAllSelected,
    selectedValueSet,
    chipTextValues,
    visibleSelectedOptions,
    hiddenSelectedOptionsCount,
    handleOpen,
    handleClose,
    handleToggleAll,
    handleToggleOption,
  } = useFilterDropdownMultiple({
    options,
    value,
    onChange,
    minOptionCount,
    exclude,
  });

  const renderOptionIcon = useCallback((option: IOption<TValue>) => {
    if (option.icon) {
      const IconComponent = option.icon;
      return <IconComponent fontSize="small" />;
    }

    if (option.iconSrc) {
      return <Styled.OptionIconImg src={option.iconSrc} alt="" />;
    }

    return null;
  }, []);

  const isApplied = !(showAllOption && isAllSelected);

  const valueLabel = useMemo(() => {
    if (!isApplied) {
      return undefined;
    }

    if (getChipText) {
      return getChipText(chipTextValues);
    }

    const labels = visibleSelectedOptions
      .map((option) => option.label)
      .join(", ");

    return hiddenSelectedOptionsCount > 0
      ? `${labels} +${hiddenSelectedOptionsCount}`
      : labels;
  }, [
    chipTextValues,
    getChipText,
    hiddenSelectedOptionsCount,
    isApplied,
    visibleSelectedOptions,
  ]);

  const chipIcon = useMemo(() => {
    if (isApplied && !getChipText && visibleSelectedOptions.length === 1) {
      return renderOptionIcon(visibleSelectedOptions[0]) ?? icon;
    }

    return icon;
  }, [getChipText, icon, isApplied, renderOptionIcon, visibleSelectedOptions]);

  const handleClear = useCallback(() => onChange(undefined), [onChange]);

  return (
    <>
      <FilterChip
        label={label}
        icon={chipIcon}
        valueLabel={valueLabel}
        defaultValueLabel={showAllOption ? "All" : undefined}
        open={open}
        onClick={handleOpen}
        onClear={handleClear}
      />

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        variant="menu"
      >
        <FilterMenuTitle sx={{ px: 2, pt: 0.5, pb: 1 }}>{label}</FilterMenuTitle>
        {showAllOption && (
          <MenuItem onClick={handleToggleAll} selected={isAllSelected}>
            <Styled.MenuItemRow>
              <Checkbox
                size="small"
                checked={isAllSelected}
                tabIndex={-1}
                disableRipple
              />
              <Typography variant="caption">All</Typography>
            </Styled.MenuItemRow>
          </MenuItem>
        )}

        {options.map((option) => {
          const isSelected = selectedValueSet.has(option.value);

          return (
            <MenuItem
              key={option.value?.toString()}
              onClick={() => handleToggleOption(option.value)}
              selected={isSelected}
            >
              <Styled.MenuItemRow>
                <Checkbox
                  size="small"
                  checked={isSelected}
                  tabIndex={-1}
                  disableRipple
                />
                {renderOptionIcon(option)}
                <Typography variant="caption">{option.label}</Typography>
              </Styled.MenuItemRow>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
